import React, { useCallback, useEffect, useState } from "react";
import { CompaniesList, UsersList, UserDetails } from "../../components";
import { CustomDialog } from "../../../../common/components";
import styles from "./DSIUsers.module.css";
import { translate } from "../../../../common/providers";
import { debounce, isNonEmptyArray, searchInTree } from "../../../../common/utils";
import { useApi, useEffectOnce } from "../../../../common/hooks";
import { BackOfficeService } from "../../../../api";

const debouncedFunction = debounce((func) => func(), 500);

export default function DSIUsers({
	companies = [],
	userRolesInCompany = [],
	onChangeBlockUser,
	onCreateUser,
	onRegeneratePassword,
}) {
	const { call: getAllUsersByCompany } = useApi(BackOfficeService.getAllUsersByCompany);
	const { call: updateUserRoleInCompany } = useApi(BackOfficeService.updateUserRoleInCompany);
	const [selectedCompany, setSelectedCompany] = useState(null);
	const [selectedUser, setSelectedUser] = useState(null);
	const [isAdding, setIsAdding] = useState(false);
	const [openConfirmDialog, setOpenConfirmDialog] = useState({ isOpen: false });
	const [usersRequest, setUsersRequest] = useState(null);
	const [searchValue, setSearchValue] = useState("");

	const handleSelectCompany = (company) => {
		if (company !== selectedCompany) {
			setSelectedCompany(company);
		}
	};
	const isValidCompany = useCallback(
		(company) => company.type === "COMPANY" || company.type === "BUSINESS_UNIT" || company.type === "CONSORTIUM",
		[]
	);
	useEffect(() => {
		if (selectedCompany) {
			setSelectedCompany(
				searchInTree({ tree: companies, idToFind: selectedCompany?.id, additionalConditions: isValidCompany })
			);
		}
	}, [companies, selectedCompany, isValidCompany]);
	useEffect(() => {
		if (selectedCompany?.id) {
			debouncedFunction(() => {
				setUsersRequest(
					() =>
						({ page, limit }) =>
							getAllUsersByCompany(
								{ companyId: selectedCompany.id },
								{ page, limit, filter: searchValue || undefined }
							)
				);
			});
		}
	}, [selectedCompany, searchValue, getAllUsersByCompany]);
	useEffectOnce(
		() => {
			handleSelectCompany(companies[0]);
		},
		[],
		() => selectedCompany === null && isNonEmptyArray(companies)
	);

	const handleSelectUser = ({ user }) => {
		if (user !== selectedUser) {
			setSelectedUser(user);
		}
	};

	const handleChangeRoleInCompany = ({ role, user, callBack }) => {
		if (!role || !user || !selectedCompany || user.companyRole === role) {
			return;
		}
		const companyId = selectedCompany.id;
		const userId = user.id;
		updateUserRoleInCompany({ companyId, userId }, role)
			.then(() => callBack())
			.catch((err) => {
				console.error(err);
			});
	};

	const handleCloseConfirmDialog = () => setOpenConfirmDialog({ isOpen: false });

	const handleChangeBlock = ({ user, callBack }) => {
		if (!user) {
			return;
		}
		setOpenConfirmDialog({
			isOpen: true,
			title: translate("dsi.dsi-users.title.ublock-user"),
			text: translate("dsi.dsi-users.text.please-confirm"),
			onSubmit: () => {
				onChangeBlockUser({ user, callBack });
				handleCloseConfirmDialog();
			},
		});
	};

	const handleRegenPassword = ({ user }) => {
		if (!user || !user.id) {
			return;
		}
		setOpenConfirmDialog({
			isOpen: true,
			title: translate("dsi.dsi-users.title.password-regeneration", { emai: user.email }),
			text: translate("dsi.dsi-users.text.please-confirm"),
			onSubmit: () => {
				onRegeneratePassword(user);
				handleCloseConfirmDialog();
			},
		});
	};
	const handleChangeSearchValue = (e) => {
		setSearchValue(e.target.value);
	};

	return (
		<>
			<div className={styles["left-panel"]}>
				<CompaniesList
					companies={companies || []}
					selectedCompany={selectedCompany}
					onClickRow={handleSelectCompany}
				/>
			</div>
			<div className={styles["middle-panel"]}>
				<UsersList
					COMPANY_ROLES={userRolesInCompany}
					searchValue={searchValue}
					selectedUser={selectedUser}
					setIsAdding={setIsAdding}
					usersRequest={usersRequest}
					onChangeLock={handleChangeBlock}
					onChangeRole={handleChangeRoleInCompany}
					onChangeSearchValue={handleChangeSearchValue}
					onClickRow={handleSelectUser}
					onRegenPassword={handleRegenPassword}
				/>
			</div>
			<UserDetails
				isAdding={isAdding}
				selectedCompany={selectedCompany}
				user={selectedUser}
				onAdd={({ payload }) => {
					onCreateUser(selectedCompany, payload);
					setIsAdding(false);
				}}
				onClose={() => {
					setSelectedUser(null);
					setIsAdding(false);
				}}
			/>
			<CustomDialog
				maxWidth="xs"
				open={openConfirmDialog && openConfirmDialog.isOpen}
				subTitle={openConfirmDialog && openConfirmDialog.text}
				title={openConfirmDialog && openConfirmDialog.title}
				onClose={handleCloseConfirmDialog}
				onSubmit={openConfirmDialog && openConfirmDialog.onSubmit}
			/>
		</>
	);
}
